// YOUR PAGES LIVE AT /<username>/… (owner call 2026-10-04: "the username in
// the url — /username/capture — avoids collisions once we allow sharing of
// different Caliber accounts, and feels more hygienic"). The username is the
// account's ofSelf username (lib/storage `username`; /api/me/username). The
// router runs under it as its basename (App), so every link and navigate in
// the app — `/capture`, `/skills/x` — lands at /<username>/capture without a
// call site knowing; this is where the rest of the app asks which base is in
// force and what the address is beneath it.
//
// The site's own public pages stay bare: the landing, sign-in, a shared card
// or a public profile (/someone), a referee's attest and vouch forms.
//
// FLUSH, NO @ (owner call 2026-10-04: "drop the @ … make any name that
// collides with a /page just unavailable"): a first segment Caliber keeps
// (lib/reserved-paths) is a page of Caliber's; any other is somebody's
// username. An old /@name/… link moves to /name/….
import { STORAGE_KEYS } from './storage';
import { firstSegment, fromOldAddress, isAddressable } from './reservedPaths';
import { NETWORK_HREF } from './captureAddress';

/** What a username can be in an address (the platform's shape). */
const USERNAME = /^[A-Za-z0-9._-]{1,64}$/;

/** A platform username usable as an app address, or no base. */
export const addressUsername = (name: string | null | undefined): string | null =>
  name && USERNAME.test(name) && isAddressable(name) ? name : null;

/** The account's ofSelf username, when this device knows it. */
export function ownUsername(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.username);
    // a username that cannot stand as an address (Caliber's names, the
    // product's, nobody's: lib/reserved-paths isAddressable) is no base
    return addressUsername(v);
  } catch { return null; }
}

/** Keep the account's username (sign-in, /api/me/username). */
export const USERNAME_EVENT = 'kalibr:username';
export function keepUsername(name: string | null | undefined): void {
  try {
    if (name === null) {
      if (localStorage.getItem(STORAGE_KEYS.username) === null) return;
      localStorage.removeItem(STORAGE_KEYS.username);
      window.dispatchEvent(new Event(USERNAME_EVENT));
      return;
    }
    if (!name || !USERNAME.test(name) || !isAddressable(name) || localStorage.getItem(STORAGE_KEYS.username) === name) return;
    localStorage.setItem(STORAGE_KEYS.username, name);
    // the router above every page moves under the new base (App)
    window.dispatchEvent(new Event(USERNAME_EVENT));
  } catch { /* storage refused: the app runs at its bare addresses */ }
}

/** How many times a lookup not answered is asked before the username settles as none. */
const LEARN_TRIES = 3;
/** The account's username, asked of the platform (/api/me/username) and kept; null when it has none yet. A lookup not
 *  answered (the network, or ofSelf's 503) is asked again a moment later, a few times, before it settles as none:
 *  only the platform's own 404 says there is none (#458: one failed first lookup drew /<you>/ as a stranger's). */
export async function learnUsername(userId: string, pause = (ms: number) => new Promise<void>((done) => { setTimeout(done, ms); })): Promise<string | null> {
  for (let tries = 0; tries < LEARN_TRIES; tries += 1) {
    // kept meanwhile (the session's read carries it too): no need to ask again
    if (tries) { await pause(1000 * 2 ** (tries - 1)); if (ownUsername()) break; }
    try {
      const r = await fetch(`/api/me/username?user_id=${encodeURIComponent(userId)}`, { credentials: 'same-origin' });
      if (r.status === 404) break;
      const d = r.ok ? ((await r.json()) as { username?: string }) : null;
      if (d?.username) { keepUsername(d.username); break; }
    } catch { /* not answered: asked again */ }
  }
  return ownUsername();
}

/** Where an app address (`/`, `/capture`, …) stands for this username: home (/) is the username itself, any other
 *  app address moves beneath it, and a public one stays bare (null). */
export function ownAddress(username: string, path: string): string | null {
  const base = baseFor(username);
  if (!base) return null;
  return path === '/' ? base : baseAddress(base, new URL(path, window.location.origin));
}

/** Into the app as this account: its username learned first, so the way in never passes through a bare address. */
export async function enterAs(userId: string, path: string, navigate: (to: string, opts?: { replace?: boolean }) => void): Promise<void> {
  const name = ownUsername() ?? await learnUsername(userId);
  const into = name ? ownAddress(name, path) : null;
  if (into) window.location.replace(into);
  else navigate(path === '/' ? '/skills' : path, { replace: true });
}

/** The base your pages live under: /<username>, or '' when the username is not known yet. */
export const baseFor = (username: string | null) => {
  const name = addressUsername(username);
  return name ? `/${name}` : '';
};

/** The username an address names (its first segment, when Caliber does not keep it), or null. */
export function usernameOf(pathname: string): string | null {
  const seg = firstSegment(pathname);
  return seg && USERNAME.test(seg) && isAddressable(seg) ? seg : null;
}

/** Generation-bound shared cards stay public even when their holder opens the link. */
export const isSharedCardPath = (pathname: string): boolean =>
  /^\/[^/]+\/[a-z0-9-]+--[0-9a-f]{12}\/?$/.test(pathname) && usernameOf(pathname) !== null;

/** The base the router is running under now (App sets it as it mounts). */
let active = '';
export function setActiveBase(base: string): void { active = base; }
export const activeBase = () => active;

/** The address beneath the base: what a route sees (`/capture`), from the bar's whole path; someone at their name is
 *  the Network page the app keeps them as (profileInApp). Never a router location's path: that is beneath the base
 *  already (a profile already its Network page, lib/cardRouter), and a one-word page in it (/everything, a card's
 *  word) would read here as somebody's name. */
export function appPath(pathname: string = typeof window === 'undefined' ? '/' : window.location.pathname): string {
  const at = profileInApp({ pathname, search: '' }, active).pathname;
  if (active && (at === active || at.startsWith(`${active}/`))) return at.slice(active.length) || '/';
  return at;
}

/** The address beneath the base, with its query — a return address the app itself reads back. */
export function appHref(): string {
  if (typeof window === 'undefined') return '/';
  const { pathname, search } = profileInApp({ pathname: window.location.pathname, search: window.location.search }, active);
  return `${appPath(pathname)}${search}`;
}

/** The site's long reads (pages/Editorial): public whoever reads them, and never under anyone's base. */
export const isSitePage = (pathname: string) => /^\/(philosophy|recruiters|roadmap|docs)\/?$/.test(pathname);

/** The site's own public pages: no account in the address — and anyone's own
 *  address (/<username>/…), which is theirs, not a bare app page to move. */
const PUBLIC = [/^\/enter\/?$/, /^\/lab(?:\/|$)/, /^\/assessment-lab\/?$/, /^\/$/, /^\/welcome\/?$/, /^\/callback(\.html)?\/?$/, /^\/step-1-register\/?$/, /^\/auth\//,
  /^\/verify\//, /^\/attest\//, /^\/p\//, /^\/c\//, /^\/vouch\//, /^\/@/];
export const isPublicPath = (pathname: string) => PUBLIC.some((re) => re.test(pathname)) || isSitePage(pathname) || usernameOf(pathname) !== null;

/** Where this address belongs under your base, or null when it is already
 *  right: your base spelled as the username is, and every bare app address
 *  beneath it; the public pages stay bare. */
export function baseAddress(base: string, loc: { pathname: string; search: string; hash: string }): string | null {
  const { pathname, search, hash } = loc;
  // AN OLD LINK, /@name/…: the same address without the @ — when a username
  // follows it (lib/reserved-paths fromOldAddress); /@/evil.com stays put
  if (pathname.startsWith('/@')) {
    const flush = fromOldAddress(pathname);
    return flush ? `${flush}${search}${hash}` : null;
  }
  if (!base) return null;
  const lower = pathname.toLowerCase(), b = base.toLowerCase();
  if (lower === b || lower.startsWith(`${b}/`)) return pathname.startsWith(base) ? null : `${base}${pathname.slice(base.length)}${search}${hash}`;
  return isPublicPath(pathname) ? null : `${base}${pathname}${search}${hash}`;
}

/** SOMEONE'S PROFILE, AT THEIR NAME (owner call 2026-10-25: "if Instagram can do it then why can't we"): for a member
 *  with a base, /<their username> is that person's profile inside the member's own app, never a page outside it, and
 *  their card read on it stands beneath (/<them>/open/<card>/read/<word>/vouch/<target>, lib/networkPlace). Their
 *  username when `pathname` is that address and not the member's own, else null. */
const PROFILE = /^\/([^/]+)((?:\/(?:open|read|vouch)\/[^/]+)*)\/?$/;
export function profileAt(pathname: string, base: string): string | null {
  if (!base) return null;
  const them = PROFILE.exec(pathname)?.[1] ?? null;
  return them && usernameOf(`/${them}`) && them.toLowerCase() !== base.slice(1).toLowerCase() ? them : null;
}

/** Someone's profile at their name, as the app keeps it: their page in your network, <base>/network/profiles/<them>,
 *  their card's reading after it; any other address as it stands. */
export function profileInApp<L extends { pathname: string; search: string }>(at: L, base: string): L {
  const them = profileAt(at.pathname, base);
  if (!them) return at;
  return { ...at, pathname: `${base}${NETWORK_HREF}/profiles/${encodeURIComponent(them)}${PROFILE.exec(at.pathname)![2]}` };
}

/** The bar's address for a page the app keeps as someone's profile (<base>/network/profiles/<them>…): /<them>…, else null. */
const KEPT = new RegExp(`^${NETWORK_HREF}/profiles/([^/]+)((?:/(?:open|read|vouch)/[^/]+)*)/?$`);
export function profileSaid(pathname: string, base: string): string | null {
  if (!base || !pathname.startsWith(`${base}/`)) return null;
  const m = KEPT.exec(pathname.slice(base.length));
  if (!m) return null;
  let them = m[1];
  try { them = decodeURIComponent(them); } catch { /* as it came */ }
  return profileAt(`/${them}`, base) ? `/${them}${m[2]}` : null;
}
